/**
 * PrivyAuthProvider - Handles Privy authentication with embedded Solana wallets.
 * 
 * This provider:
 * 1. Initializes Privy with Solana chain configuration
 * 2. Enables email + social login methods (Google, Twitter, Discord)
 * 3. Creates embedded Solana wallets for new users
 * 4. Exchanges Privy tokens for backend JWTs
 * 5. Exposes usePrivyAuth() hook for auth state and methods
 */

import {
  createContext,
  useCallback,
  useEffect,
  useState,
  useRef,
  type ReactNode,
} from "react";
import {
  PrivyProvider,
  usePrivy,
  useLogin as usePrivyLogin,
  useLogout as usePrivyLogout,
  type User as PrivyUser,
} from "@privy-io/react-auth";
import { 
  toSolanaWalletConnectors, 
  useFundWallet,
  useExportWallet 
} from "@privy-io/react-auth/solana";
import { useQueryClient } from "@tanstack/react-query";
import { createSolanaRpc, createSolanaRpcSubscriptions } from "@solana/kit";
import { storeToken, clearStoredToken } from "./auth";
import { useAccountStore } from "./store/account-store";
import { useTheme } from "@/theme/theme-context";

import { API_BASE_URL } from "@/backend/config";
const PRIVY_APP_ID = import.meta.env.VITE_PRIVY_APP_ID;
const SOLANA_RPC_ENDPOINT = import.meta.env.VITE_SOLANA_RPC_ENDPOINT || "https://api.mainnet-beta.solana.com";
// Convert HTTP RPC URL to WebSocket URL for subscriptions
const SOLANA_WS_ENDPOINT = SOLANA_RPC_ENDPOINT.replace('https://', 'wss://').replace('http://', 'ws://');

// Solana RPC configuration for Privy
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const solanaRpcs: any = {
  'solana:mainnet': {
    rpc: createSolanaRpc(SOLANA_RPC_ENDPOINT),
    rpcSubscriptions: createSolanaRpcSubscriptions(SOLANA_WS_ENDPOINT),
    blockExplorerUrl: 'https://explorer.solana.com',
  },
  'solana:devnet': {
    rpc: createSolanaRpc('https://api.devnet.solana.com'),
    rpcSubscriptions: createSolanaRpcSubscriptions('wss://api.devnet.solana.com'),
    blockExplorerUrl: 'https://explorer.solana.com/?cluster=devnet',
  },
};

/** Response from backend token exchange */
interface ExchangeTokenResponse {
  token: string;
  expires_at: number;
  user_id: string;
  login_method?: string;
  /** Whether this is a brand new user (first signup) - for Meta Pixel Lead tracking */
  is_new_user?: boolean;
}

/** Exchange Privy token for backend JWT */
async function exchangePrivyToken(
  privyToken: string,
  walletAddress: string,
  email?: string | null,
  authProvider?: string | null
): Promise<ExchangeTokenResponse | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/exchange-privy`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        privy_token: privyToken,
        wallet_address: walletAddress,
        email: email || null,  // Include email if available
        auth_provider: authProvider || null,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Exchange failed" }));
      console.error("[PrivyAuth] Token exchange failed:", error);
      return null;
    }

    return await response.json();
  } catch (err) {
    console.error("[PrivyAuth] Token exchange error:", err);
    return null;
  }
}

export interface PrivyAuthContextValue {
  /** Whether Privy has initialized */
  ready: boolean;
  /** Whether user is authenticated via Privy */
  authenticated: boolean;
  /** Privy user object */
  user: PrivyUser | null;
  /** Embedded Solana wallet address */
  solanaWalletAddress: string | null;
  /** Backend JWT token (for WebSocket auth) */
  backendToken: string | null;
  /** User ID (database UUID from backend) */
  userId: string | null;
  /** Login method from database: 'wallet', 'privy', or 'both' */
  loginMethod: string | null;
  /** Whether this is a brand new user (first signup) - for Meta Pixel Lead tracking */
  isNewUser: boolean;
  /** Trigger Privy login modal */
  login: () => void;
  /** Logout from Privy */
  logout: () => Promise<void>;
  /** Open Privy's fund wallet modal (buy crypto with card or transfer) */
  fundWallet: () => Promise<void>;
  /** Export Privy embedded wallet (shows private key in secure modal) */
  exportWallet: (options?: { address?: string }) => Promise<void>;
}

export const PrivyAuthContext = createContext<PrivyAuthContextValue | null>(null);

// Solana wallet connectors for Privy
const solanaConnectors = toSolanaWalletConnectors();

interface PrivyAuthProviderInnerProps {
  children: ReactNode;
}

/**
 * Inner provider that uses Privy hooks (must be inside PrivyProvider)
 */
function PrivyAuthProviderInner({ children }: PrivyAuthProviderInnerProps) {
  const { ready, authenticated, user, getAccessToken } = usePrivy();
  const { login } = usePrivyLogin();
  const { logout: privyLogout } = usePrivyLogout();
  const { fundWallet: privyFundWallet } = useFundWallet();
  const { exportWallet: privyExportWallet } = useExportWallet(); // Solana-specific export hook
  const queryClient = useQueryClient();
  
  const [backendToken, setBackendToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [loginMethod, setLoginMethod] = useState<string | null>(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const exchangedTokensRef = useRef<Set<string>>(new Set());

  // Get the embedded Solana wallet address
  // Privy wallet accounts have an 'address' property when type is 'wallet'
  const solanaWallet = user?.linkedAccounts?.find(
    (account) => account.type === "wallet" && account.chainType === "solana"
  ) as { address: string } | undefined;
  const solanaWalletAddress = solanaWallet?.address ?? null;

  // Exchange Privy token for backend JWT when authenticated
  useEffect(() => {
    const doExchange = async () => {
      if (!ready || !authenticated || !user || !solanaWalletAddress) {
        // Only clear our own state - DON'T clear localStorage tokens
        // as they might belong to wallet auth users
        setBackendToken(null);
        setUserId(null);
        setLoginMethod(null);
        setIsNewUser(false);
        // DO NOT clear localStorage here - wallet users need their tokens!
        // clearStoredToken();
        return;
      }

      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          console.warn("[PrivyAuth] No access token available");
          return;
        }

        // Create a unique key for this token + wallet combination
        const tokenKey = `${accessToken.substring(0, 20)}-${solanaWalletAddress}`;
        
        // Skip if we've already exchanged this token
        if (exchangedTokensRef.current.has(tokenKey)) {
          return;
        }

        console.log("[PrivyAuth] Exchanging Privy token for backend JWT...");
        exchangedTokensRef.current.add(tokenKey);

        // Extract email from Privy user object if available.
        // user.email.address is set for email OTP logins.
        // user.google.email is set for Google OAuth logins.
        const userEmail = user?.email?.address
          || (user?.google as { email?: string } | undefined)?.email
          || null;
        const userWithOptionalProviders = user as unknown as {
          apple?: unknown;
          twitter?: unknown;
        };
        const authProvider =
          user?.google ? "google" :
          // Privy user type varies by SDK versions; check known keys defensively.
          (userWithOptionalProviders.apple ? "apple" :
          (userWithOptionalProviders.twitter ? "x" :
          ("email" in user && user?.email?.address ? "email" : null)));

        const result = await exchangePrivyToken(accessToken, solanaWalletAddress, userEmail, authProvider);
        if (result) {
          console.log("[PrivyAuth] Got backend JWT for user:", result.user_id, `login_method=${result.login_method}`, `is_new_user=${result.is_new_user}`, userEmail ? `(email: ${userEmail})` : "");
          setBackendToken(result.token);
          setUserId(result.user_id);  // Store the database UUID
          setLoginMethod(result.login_method || null);  // Store login method
          setIsNewUser(result.is_new_user ?? false);  // Track if this is a new signup
          // Store token in localStorage so API client can access it
          storeToken(result.token, result.expires_at, solanaWalletAddress, result.user_id, userEmail || undefined, result.login_method);
        } else {
          console.warn("[PrivyAuth] Token exchange failed - WebSocket may not work");
        }
      } catch (err) {
        console.error("[PrivyAuth] Error getting access token:", err);
      }
    };

    doExchange();
  }, [ready, authenticated, user, solanaWalletAddress, getAccessToken]);

  // Handle logout
  const logout = useCallback(async () => {
    try {
      await privyLogout();
      setBackendToken(null);
      setUserId(null);
      setLoginMethod(null);
      setIsNewUser(false);
      exchangedTokensRef.current.clear();
      // Clear token from localStorage
      clearStoredToken();
      
      // Clear React Query caches - critical for re-login to work properly
      // Using removeQueries ensures they're fully cleared (not just stale)
      queryClient.removeQueries({ queryKey: ["account-sync"] });
      queryClient.removeQueries({ queryKey: ["backend-account"] });
      queryClient.removeQueries({ queryKey: ["backend-positions"] });
      queryClient.removeQueries({ queryKey: ["backend-orders"] });
      // Clear notification queries
      queryClient.removeQueries({ queryKey: ["user-notifications"] });
      queryClient.removeQueries({ queryKey: ["notification-preferences"] });
      
      // Clear Zustand account store - critical for clearing old user's data
      useAccountStore.getState().reset();
    } catch (err) {
      console.error("[PrivyAuth] Logout error:", err);
    }
  }, [privyLogout, queryClient]);

  // Handle fund wallet - opens Privy's built-in funding modal
  // This allows users to buy crypto with card (Moonpay) or transfer from external wallet
  const fundWallet = useCallback(async () => {
    if (!solanaWalletAddress) {
      console.warn("[PrivyAuth] No wallet address to fund");
      return;
    }
    try {
      // Use the Solana-specific fundWallet from @privy-io/react-auth/solana
      // The funding modal will show options: Pay with card, Transfer from exchange, 
      // Transfer from wallet, Receive funds
      // Note: Do NOT pass asset parameter - let MoonPay handle currency selection
      // Passing asset: "USDC" causes silent failures (404 errors)
      await privyFundWallet({
        address: solanaWalletAddress,
        // No options - let user/MoonPay choose the asset
      });
    } catch (err) {
      console.error("[PrivyAuth] Fund wallet error:", err);
    }
  }, [solanaWalletAddress, privyFundWallet]);

  // Handle export wallet - opens Privy's secure export modal
  // This shows the user's embedded wallet private key in a secure iframe
  const exportWallet = useCallback(async (options?: { address?: string }) => {
    if (!solanaWalletAddress) {
      console.warn("[PrivyAuth] No wallet address to export");
      return;
    }
    try {
      // Use Privy's built-in export wallet feature
      // This opens a secure modal on a different origin to display the private key
      await privyExportWallet({ address: options?.address || solanaWalletAddress });
    } catch (err) {
      console.error("[PrivyAuth] Export wallet error:", err);
      throw err;
    }
  }, [solanaWalletAddress, privyExportWallet]);

  const value: PrivyAuthContextValue = {
    ready,
    authenticated,
    user,
    solanaWalletAddress,
    backendToken,
    userId,
    loginMethod,
    isNewUser,
    login,
    logout,
    fundWallet,
    exportWallet,
  };

  return (
    <PrivyAuthContext.Provider value={value}>
      {children}
    </PrivyAuthContext.Provider>
  );
}

interface PrivyAuthProviderProps {
  children: ReactNode;
}

/**
 * Main Privy auth provider that wraps PrivyProvider
 * Dynamically adapts to the app's dark/light theme
 */
export function PrivyAuthProvider({ children }: PrivyAuthProviderProps) {
  const { theme } = useTheme(); // Get current theme from app context
  
  if (!PRIVY_APP_ID) {
    console.warn("[PrivyAuth] VITE_PRIVY_APP_ID not configured, Privy auth disabled");
    // Return children without Privy context if not configured
    return <>{children}</>;
  }

  // Dynamic logo based on theme - use SVG for better quality
  const logoUrl = theme === "dark" 
    ? `${import.meta.env.VITE_FRONTEND_URL || "https://dev.nordex.trade"}/Nordex_Logo_Color_White.svg`
    : `${import.meta.env.VITE_FRONTEND_URL || "https://dev.nordex.trade"}/Nordex_Logo_Color_Black.svg`;

  return (
    <PrivyProvider
      appId={PRIVY_APP_ID}
      config={{
        appearance: {
          theme: theme, // Dynamic theme! Adapts to app's dark/light mode
          accentColor: "#2ebd85", // Nordex brand green
          logo: logoUrl, // Dynamic SVG logo based on theme
        },
        // Email + Google login - Google OAuth must also be enabled in the Privy Dashboard
        loginMethods: ["email", "google"],
        embeddedWallets: {
          solana: {
            createOnLogin: "users-without-wallets",
          },
          showWalletUIs: true,
        },
        externalWallets: {
          solana: {
            connectors: solanaConnectors,
          },
        },
        solana: {
          rpcs: solanaRpcs,
        },
        // Enable MoonPay sandbox mode for testing (use test cards, no real money)
        // Note: Privy manages MoonPay through their own partnership - you cannot use your own API keys
        // Contact Privy support to enable MoonPay for your app
        fundingMethodConfig: {
          moonpay: {
            useSandbox: true,
          },
        },
      }}
    >
      <PrivyAuthProviderInner>{children}</PrivyAuthProviderInner>
    </PrivyProvider>
  );
}
